import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useNotify } from "../../../context/NotificationContext";
import { Card, Table, Input, Button, Typography, Tag, Select } from "antd";
import { PlusOutlined, SearchOutlined, EditOutlined, DownloadOutlined } from "@ant-design/icons";
import videoService from "../../../services/videoService";
import masterService from "../../../services/masterService";
import { useCommon } from "../../../context/CommonContext";

const { Title, Text } = Typography;
const { Search } = Input;

const ShowList = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const { setPageTitle } = useCommon();
  // State management
  const [loading, setLoading] = useState(false);
  const [shows, setShows] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} shows`,
  });

  // Filters
  const [filters, setFilters] = useState({
    search: "",
  });

  // What is typed in the box, kept separate from filters.search so the input stays
  // responsive while the term it actually searches on settles. filters.search is
  // what getShows depends on, so debouncing into it is what collapses a typed word
  // down to a single request.
  const [searchInput, setSearchInput] = useState("");
  const [genres, setGenres] = useState([]);
  const [categories, setCategories] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [tags, setTags] = useState([]);

  const [selectedGenres, setSelectedGenres] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedRatings, setSelectedRatings] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [hasMovieFileFilter, setHasMovieFileFilter] = useState(undefined);
  const [hasSubtitleFileFilter, setHasSubtitleFileFilter] = useState(undefined);
  const [hasTrailerFilter, setHasTrailerFilter] = useState(undefined);
  const [hasClipFilter, setHasClipFilter] = useState(undefined);

  const fetchCategories = async () => {
    const response = await masterService.getMasterCategory({ isAll: true });
    const data = response.data.data;
    console.log("API Response Data for Categories:", data);
    const list = data?.map((item) => ({
      value: item.category_id || item.vc_id,
      label: item.category_name || item.vc_name,
    }));
    console.log("list===>categories", list);
    setCategories(list);
  };

  const fetchGenres = async () => {
    const response = await masterService.getGenres({ isAll: true });
    const data = response.data.data;
    const list = data?.map((item) => ({
      value: item.genre_id,
      label: item.genre_name,
    }));
    console.log("list===>genres", list);
    setGenres(list);
  };

  const fetchRatings = async () => {
    const response = await videoService.getAllVideoRatings({ isAll: true });
    const data = response.data.data;
    const list = data?.map((item) => ({
      value: item.vr_id,
      label: item.rating_name,
    }));
    console.log("list===>ratings", list);
    setRatings(list);
  };

  const fetchTags = async () => {
    const response = await masterService.getTagList({ isAll: true });
    const data = response.data.data;
    const list = data?.map((item) => ({
      value: item.tag_id,
      label: item.tag_name,
    }));
    console.log("list===>tags", list);
    setTags(list);
  };

  useEffect(() => {
    fetchCategories();
    fetchGenres();
    fetchRatings();
    fetchTags();
    setPageTitle("Movies");
  }, []);

  // Typing fired one request per keystroke, because the search term is a
  // dependency of getShows and the effect below re-runs whenever it is rebuilt.
  // The term is now debounced into that state on the same 300ms the users and
  // promos lists use, so a typed word costs one request instead of one per letter.
  const searchDebounceRef = useRef(null);
  const SEARCH_DEBOUNCE_MS = 300;
  // Monotonic request id — responses from superseded requests are dropped so a
  // slow earlier search can't overwrite the results of the latest one.
  const requestSeqRef = useRef(0);

  const getShows = useCallback(
    async (params = {}) => {
      const requestId = ++requestSeqRef.current;
      try {
        setLoading(true);

        const requestParams = {
          perPage: pagination.pageSize,
          currentPage: pagination.current,
          searchText: filters.search,
          status: "Y",
          genre_list: selectedGenres,
          mscategory_list: selectedCategories,
          rating_list: selectedRatings,
          tag_list: selectedTags,
          ...(hasMovieFileFilter && {
            has_movie_file: hasMovieFileFilter === "YES" ? "Y" : "N",
          }),
          ...(hasSubtitleFileFilter && {
            has_subtitle_file: hasSubtitleFileFilter === "YES" ? "Y" : "N",
          }),
          ...(hasTrailerFilter && {
            has_trailer: hasTrailerFilter === "YES" ? "Y" : "N",
          }),
          ...(hasClipFilter && {
            has_clip: hasClipFilter === "YES" ? "Y" : "N",
          }),
          ...params,
        };

        console.log("Fetching shows with params:", requestParams);
        const response = await videoService.getAllVideoShow(requestParams);
        if (requestId !== requestSeqRef.current) return; // superseded by a newer request
        console.log("Shows API Response:", response);

        if (response.data && response.data.status) {
          const showsData = response.data.data?.data || [];
          const paginationData = response.data.data?.pagination || {};

          console.log("Shows data:", showsData);
          console.log("Pagination data:", paginationData);

          // Ensure showsData is an array
          if (Array.isArray(showsData)) {
            setShows(showsData);
            setPagination((prev) => ({
              ...prev,
              total: paginationData.total || showsData.length,
              current: paginationData.currentPage || prev.current,
            }));
          } else {
            console.error("Shows data is not an array:", showsData);
            setShows([]);
          }
        } else {
          notify.error({ message: "Failed to fetch video shows" });
          setShows([]);
        }
      } catch (error) {
        if (requestId !== requestSeqRef.current) return;
        console.error("Error fetching shows:", error);
        notify.error({ message: "Failed to fetch video shows" });
        setShows([]);
      } finally {
        // Only the newest request clears the spinner — an overtaken one finishing
        // early would otherwise show the list as loaded while it is still loading.
        if (requestId === requestSeqRef.current) setLoading(false);
      }
    },
    [
      filters.search,
      pagination.pageSize,
      pagination.current,
      selectedGenres,
      selectedCategories,
      selectedRatings,
      selectedTags,
      hasMovieFileFilter,
      hasSubtitleFileFilter,
      hasTrailerFilter,
      hasClipFilter,
    ]
  );

  // Load shows data
  useEffect(() => {
    getShows();
  }, [getShows]);

  // Treat value as "has movie file" (API may send "YES", "Y", true, 1, etc.)
  const hasMovieFile = (row) => {
    const v = row?.has_movie_file ?? row?.hasMovieFile;
    if (v === undefined || v === null) return false;
    if (typeof v === "boolean") return v;
    if (typeof v === "number") return v !== 0;
    const s = String(v).toUpperCase();
    return s === "YES";
  };

  // Movie file filter is applied server-side via has_movie_file param so pagination reflects filtered total

  const handleSearchChange = (e) => {
    const value = e.target.value ?? "";
    setSearchInput(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      const term = value.trim();
      setFilters((prev) => (prev.search === term ? prev : { ...prev, search: term }));
      setPagination((prev) => (prev.current === 1 ? prev : { ...prev, current: 1 }));
      searchDebounceRef.current = null;
    }, SEARCH_DEBOUNCE_MS);
  };

  // Enter key / search icon applies immediately, cancelling any pending debounce
  // so the term is not searched twice.
  const handleSearchNow = () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    const term = searchInput.trim();
    setFilters((prev) => (prev.search === term ? prev : { ...prev, search: term }));
    setPagination((prev) => (prev.current === 1 ? prev : { ...prev, current: 1 }));
  };

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  // Handle table change (pagination, sorting, filtering)
  const handleTableChange = (paginationInfo, tableFilters, sorter) => {
    const { current, pageSize } = paginationInfo;

    setPagination((prev) => ({
      ...prev,
      current,
      pageSize,
    }));

    getShows({
      currentPage: current,
      perPage: pageSize,
      searchText: filters.search,
      status: "Y",
      genre_list: selectedGenres,
      mscategory_list: selectedCategories,
      rating_list: selectedRatings,
      tag_list: selectedTags,
      ...(hasMovieFileFilter && {
        has_movie_file: hasMovieFileFilter === "YES" ? "Y" : "N",
      }),
      ...(hasSubtitleFileFilter && {
        has_subtitle_file: hasSubtitleFileFilter === "YES" ? "Y" : "N",
      }),
      ...(hasTrailerFilter && {
        has_trailer: hasTrailerFilter === "YES" ? "Y" : "N",
      }),
      ...(hasClipFilter && {
        has_clip: hasClipFilter === "YES" ? "Y" : "N",
      }),
    });
  };

  // Handle add show
  const handleAddShow = () => {
    navigate("/movies/add/new");
  };

  // Handle export all movies to Excel
  const handleExportAllExcel = async () => {
    try {
      const resp = await videoService.exportAllVideoShowsExcel();
      const blob = new Blob([resp.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "all-movies.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      notify.error({ message: "Failed to download Excel file" });
    }
  };

  // Handle edit show
  const handleEditShow = (videoShowId) => {
    navigate(`/movies/edit/${videoShowId}`);
  };

  // Table columns
  const columns = [
    {
      title: "Show Title",
      dataIndex: "video_show_title",
      key: "video_show_title",
      defaultSortOrder: "ascend",
      sorter: (a, b) =>
        (a.video_show_title || "").localeCompare(b.video_show_title || ""),
      render: (text) => (
        <Text strong style={{ fontSize: "14px" }}>
          {text}
        </Text>
      ),
    },
    {
      title: "Run Time",
      dataIndex: "vs_runtime",
      key: "vs_runtime",
      width: 120,
      render: (text) => (
        <Text style={{ fontSize: "14px" }}>{text ? `${text} min` : "N/A"}</Text>
      ),
    },
    {
      title: "Release Date",
      dataIndex: "vs_release_date_display",
      key: "vs_release_date_display",
      width: 180,
      render: (text) => (
        <Text style={{ fontSize: "13px" }}>{text || "No Date"}</Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "video_show_is_active",
      key: "video_show_is_active",
      width: 100,
      align: "center",
      render: (status) => (
        <Tag color={status === "Y" ? "green" : "red"}>
          {status === "Y" ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Trailer",
      dataIndex: "has_trailer",
      key: "has_trailer",
      width: 90,
      align: "center",
      render: (status) => (
        <Tag color={status === "YES" ? "green" : "red"}>{status || "NO"}</Tag>
      ),
    },
    {
      title: "Clip",
      dataIndex: "has_clip",
      key: "has_clip",
      width: 80,
      align: "center",
      render: (status) => (
        <Tag color={status === "YES" ? "green" : "red"}>{status || "NO"}</Tag>
      ),
    },
    {
      title: "Movie",
      dataIndex: "has_movie_file",
      key: "has_movie_file",
      width: 90,
      align: "center",
      render: (_, record) => {
        const yes = hasMovieFile(record);
        return <Tag color={yes ? "green" : "red"}>{yes ? "YES" : "NO"}</Tag>;
      },
    },
    {
      title: "Subtitle",
      dataIndex: "has_subtitle_file",
      key: "has_subtitle_file",
      width: 100,
      align: "center",
      render: (status) => (
        <Tag color={status === "YES" ? "green" : "red"}>{status || "NO"}</Tag>
      ),
    },
    {
      title: "Actions",
      key: "action",
      width: 160,
      fixed: "right",
      render: (text, record) => (
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditShow(record.video_show_id)}
            size="small"
          >
            Edit
          </Button>
        </div>
      ),
    },
  ];

  // Header content with user info

  return (
    <Card className="">
      <div style={{ marginBottom: "16px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <Title level={4} style={{ margin: 0 }}>
            Video Shows ({pagination.total})
          </Title>

          <div style={{ display: "flex", gap: "8px" }}>
            <Button icon={<DownloadOutlined />} onClick={handleExportAllExcel}>
              Export All to Excel
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddShow}
            >
              Add Movie
            </Button>
          </div>
        </div>

        <div
          className="grid grid-cols-4 gap-4 items-center flex-wrap"
        >
          <Search
            placeholder="Search shows..."
            allowClear
            value={searchInput}
            onChange={handleSearchChange}
            onSearch={handleSearchNow}
            enterButton={<SearchOutlined />}
          />
          <Select
            options={genres}
            placeholder="Select genre"
            allowClear
            showSearch
            optionFilterProp="label"
            mode="multiple"
            value={selectedGenres}
            onChange={setSelectedGenres}
          />
          <Select
            options={ratings}
            placeholder="Select rating"
            allowClear
            showSearch
            optionFilterProp="label"
            mode="multiple"
            value={selectedRatings}
            onChange={setSelectedRatings}
          />
          <Select
            options={tags}
            placeholder="Select tag"
            allowClear
            showSearch
            optionFilterProp="label"
            mode="multiple"
            value={selectedTags}
            onChange={setSelectedTags}
          />
          <Select
            options={categories}
            placeholder="Select category"
            allowClear
            showSearch
            optionFilterProp="label"
            mode="multiple"
            value={selectedCategories}
            onChange={(values) => {
              setSelectedCategories(values);
              setPagination((prev) => ({ ...prev, current: 1 }));
            }}
          />
          <Select
            options={[
              { value: "YES", label: "Yes" },
              { value: "NO", label: "No" },
            ]}
            placeholder="Movie file"
            allowClear
            value={hasMovieFileFilter}
            onChange={(value) => {
              setHasMovieFileFilter(value);
              setPagination((prev) => ({ ...prev, current: 1 }));
            }}
            style={{ minWidth: 120 }}
          />
          <Select
            options={[
              { value: "YES", label: "Yes" },
              { value: "NO", label: "No" },
            ]}
            placeholder="Subtitle file"
            allowClear
            value={hasSubtitleFileFilter}
            onChange={(value) => {
              setHasSubtitleFileFilter(value);
              setPagination((prev) => ({ ...prev, current: 1 }));
            }}
            style={{ minWidth: 120 }}
          />
          <Select
            options={[
              { value: "YES", label: "Yes" },
              { value: "NO", label: "No" },
            ]}
            placeholder="Trailer"
            allowClear
            value={hasTrailerFilter}
            onChange={(value) => {
              setHasTrailerFilter(value);
              setPagination((prev) => ({ ...prev, current: 1 }));
            }}
            style={{ minWidth: 120 }}
          />
          <Select
            options={[
              { value: "YES", label: "Yes" },
              { value: "NO", label: "No" },
            ]}
            placeholder="Clip"
            allowClear
            value={hasClipFilter}
            onChange={(value) => {
              setHasClipFilter(value);
              setPagination((prev) => ({ ...prev, current: 1 }));
            }}
            style={{ minWidth: 120 }}
          />
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={shows}
        rowKey="video_show_id"
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        scroll={{ x: 1200 }}
        size="middle"
        locale={{
          emptyText: "No video shows found",
          filterConfirm: "OK",
          filterReset: "Reset",
          filterEmptyText: "No filters",
        }}
      />
    </Card>
  );
};

export default ShowList;
